/**
 * Utility functions for transforming data in the rebooking process
 */

/**
 * Helper function to check if a product is cancelled
 * Uses robust cancellation checking to handle boolean, string, and number values
 * @param product Product to check
 * @returns True if the product is cancelled
 */
const isProductCancelled = (product: any): boolean => {
  if (!product) return false
  
  let isCancelled = false
  
  if (typeof product.cancelled === 'boolean') {
    isCancelled = product.cancelled === true
  } else if (typeof product.cancelled === 'string') {
    isCancelled = product.cancelled === 'true' || product.cancelled === '1'
  } else if (typeof product.cancelled === 'number') {
    isCancelled = product.cancelled === 1
  }
  
  return isCancelled
}

/**
 * Filters out cancelled segments to get only active segments
 * Uses robust cancellation checking to handle boolean, string, and number values
 * A segment is considered cancelled if:
 * 1. The segment itself has a cancelled flag set to true, OR
 * 2. All required products in the segment are cancelled
 * @param segments Array of segments to filter
 * @returns Array of active (non-cancelled) segments
 */
export const getActiveSegments = (segments: any[] = []): any[] => {
  console.log('[DEBUG] getActiveSegments called with segments:', segments.length)
  
  const activeSegments = segments.filter((segment: any) => {
    console.log(`[DEBUG] Processing segment ${segment.id}:`, {
      segmentId: segment.id,
      segmentCancelled: segment.cancelled,
      requiredProductsCount: segment.required_products?.length || 0
    })
    
    // Check if segment itself is explicitly cancelled
    let isSegmentCancelled = false
    
    if (typeof segment.cancelled === 'boolean') {
      isSegmentCancelled = segment.cancelled === true
    } else if (typeof segment.cancelled === 'string') {
      isSegmentCancelled = segment.cancelled === 'true' || segment.cancelled === '1'
    } else if (typeof segment.cancelled === 'number') {
      isSegmentCancelled = segment.cancelled === 1
    }
    
    console.log(`[DEBUG] Segment ${segment.id} explicitly cancelled:`, isSegmentCancelled)
    
    // If segment is explicitly cancelled, filter it out
    if (isSegmentCancelled) {
      console.log(`[DEBUG] Segment ${segment.id} FILTERED OUT - explicitly cancelled`)
      return false
    }
    
    // Check if all required products are cancelled
    const requiredProducts = segment.required_products || []
    
    // If there are no required products, consider the segment active
    if (requiredProducts.length === 0) {
      console.log(`[DEBUG] Segment ${segment.id} KEPT - no required products`)
      return true
    }
    
    // Check each required product
    console.log(`[DEBUG] Checking ${requiredProducts.length} required products for segment ${segment.id}:`)
    requiredProducts.forEach((product: any, index: number) => {
      const cancelled = isProductCancelled(product)
      console.log(`[DEBUG] Product ${index + 1}:`, {
        uuid: product.uuid,
        cancelled: product.cancelled,
        isProductCancelled: cancelled,
        cancellationReason: product.cancellation_reason
      })
    })
    
    // Check if all required products are cancelled
    const allRequiredProductsCancelled = requiredProducts.every((product: any) => isProductCancelled(product))
    
    console.log(`[DEBUG] Segment ${segment.id} all products cancelled:`, allRequiredProductsCancelled)
    
    // Filter out segments where all required products are cancelled
    const keepSegment = !allRequiredProductsCancelled
    console.log(`[DEBUG] Segment ${segment.id} ${keepSegment ? 'KEPT' : 'FILTERED OUT'} - ${allRequiredProductsCancelled ? 'all products cancelled' : 'has active products'}`)
    
    return keepSegment
  })
  
  console.log(`[DEBUG] getActiveSegments result: ${activeSegments.length}/${segments.length} segments kept`, 
    activeSegments.map(s => s.id))
  
  return activeSegments
}

/**
 * Transforms a booking tariff segment into a standard segment format
 */
export const transformSegment = (segment: any, direction: 'outbound' | 'inbound') => {
  if (!segment) return null
  
  return {
    id: segment.id,
    ref_id: `segment_${segment.id}`,
    direction: direction.toUpperCase(),
    schedule_date: segment.departure_date_time,
    origin_station_code: segment.departure_station?.short_code || '',
    destination_station_code: segment.arrival_station?.short_code || '',
    service_identifier: segment.service_identifier || '',
    service_name: segment.service_name || '',
    service_type: segment.service_type || '',
    tariff_group_code: segment.tariff_group_code || '',
    is_international: segment.is_international || false,
    required_products: segment.required_products || [],
    additional_products: segment.additional_products || [],
    items: segment.required_products?.map((rp: any) => ({
      passenger_id: rp.passenger_id,
      tariff_code: rp.tariff_code,
      service_name: rp.name,
    })) || [],
    additional_tariff_conditions: {
      journey_od: {
        origin: segment.departure_station?.short_code || '',
        destination: segment.arrival_station?.short_code || '',
      },
    },
  }
}

/**
 * Transforms booking data to include standardized segments
 */
export const transformBookingData = (bookingData: any) => {
  if (!bookingData) return null
  
  const outboundSegments = bookingData.outbound_booking_tariff_segments || []
  const inboundSegments = bookingData.inbound_booking_tariff_segments || []
  
  return {
    ...bookingData,
    segments: [
      ...(outboundSegments.map((s: any) => transformSegment(s, 'outbound')) || []),
      ...(inboundSegments.map((s: any) => transformSegment(s, 'inbound')) || []),
    ],
    outbound_booking_tariff_segments: outboundSegments.map((s: any) => 
      transformSegment(s, 'outbound')
    ),
    inbound_booking_tariff_segments: inboundSegments.map((s: any) => 
      transformSegment(s, 'inbound')
    ),
  }
}

/**
 * Checks if booking has seats in active (non-cancelled) segments only
 */
export const checkIfBookingHasSeats = (bookingData: any): boolean => {
  // Check active outbound segments only
  const activeOutboundSegments = getActiveSegments(bookingData.outbound_booking_tariff_segments)
  for (const segment of activeOutboundSegments) {
    if (segment.required_products) {
      for (const product of segment.required_products) {
        if (product.seat) {
          return true
        }
      }
    }
  }
  
  // Check active inbound segments only
  const activeInboundSegments = getActiveSegments(bookingData.inbound_booking_tariff_segments)
  for (const segment of activeInboundSegments) {
    if (segment.required_products) {
      for (const product of segment.required_products) {
        if (product.seat) {
          return true
        }
      }
    }
  }
  
  return false
}

/**
 * Gets the segment reference ID for a service
 * 
 * @param serviceId The service identifier or leg ID from journey search
 * @param direction Direction of travel ('outbound' or 'inbound')
 * @param bookingData Original booking data
 * @returns A segment reference ID to use in the rebooking request
 */
export const getSegmentRefIdForService = (
  serviceId: string, 
  direction: 'outbound' | 'inbound',
  bookingData: any
) => {
  // If serviceId is a full leg ID from journey search (contains semicolons), use it directly
  // This is the format expected by the API: {service_identifier};{origin_station_code};{destination_station_code}
  if (serviceId && serviceId.includes(';')) {
    console.log(`[DEBUG] Using leg ID directly as segment ref_id: ${serviceId}`)
    return serviceId
  }
  
  // Fall back to the old behavior if not a full leg ID
  const segments = direction === 'outbound' 
    ? bookingData.outbound_booking_tariff_segments || []
    : bookingData.inbound_booking_tariff_segments || []
  
  const segment = segments.find((s: any) => s.service_name === serviceId)
  if (segment) {
    return `segment_${segment.id}`
  }
  
  // If we still can't find a match, log a warning
  console.warn(`[DEBUG] Could not find segment for service ID: ${serviceId}`)
  return undefined
}
